import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import CalendlyModal from "@/components/CalendlyModal";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import {
  Brain,
  Sparkles,
  MessageSquare,
  Target,
  BarChart3,
  Users,
  Settings,
  Shield,
  Clock,
  FileText,
  Zap,
  CheckCircle,
  ArrowRight,
  Lock,
  Server,
  Trash2,
  Bell,
  Filter,
  Download,
  Sliders,
  Calendar,
  HelpCircle,
  PhoneForwarded,
} from "lucide-react";

const useScrollReveal = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return { ref, isVisible };
};

const FeatureCard = ({
  icon: Icon,
  title,
  description,
  delay = 0,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  delay?: number;
}) => {
  const { ref, isVisible } = useScrollReveal();

  return (
    <div
      ref={ref}
      className="border-t border-foreground/20 p-5 transition-colors hover:border-primary/40"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(30px)",
        transition: `all 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
      }}
    >
      <div className="w-10 h-10 border border-border flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-primary" />
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
      <p className="text-muted-foreground text-sm leading-relaxed">{description}</p>
    </div>
  );
};

const qualificationFeatures = [
  {
    icon: Target,
    title: "Détection d'intention",
    description: "Identifie automatiquement si l'appelant souhaite acheter, vendre, louer ou obtenir une estimation.",
  },
  {
    icon: FileText,
    title: "Extraction d'informations",
    description: "Capture le budget, le secteur recherché, le timing et les critères spécifiques de chaque prospect.",
  },
  {
    icon: Zap,
    title: "Priorisation automatique",
    description: "Classe chaque appel par niveau d'urgence pour traiter les opportunités chaudes en priorité.",
  },
  {
    icon: Sparkles,
    title: "Synthèse IA",
    description: "Génère un résumé instantané de chaque appel avec les points clés à retenir.",
  },
  {
    icon: MessageSquare,
    title: "Transcription complète",
    description: "Conserve l'historique intégral de chaque conversation pour une consultation ultérieure.",
  },
  {
    icon: Brain,
    title: "Questions dynamiques",
    description: "S'adapte au contexte de l'appel pour poser les bonnes questions au bon moment.",
  },
];

const dashboardFeatures = [
  {
    icon: Filter,
    title: "Filtres avancés",
    description: "Recherchez et filtrez vos tickets par statut, priorité, intention ou période.",
  },
  {
    icon: BarChart3,
    title: "Statistiques en temps réel",
    description: "Suivez vos métriques clés : appels traités, temps gagné, taux de qualification.",
  },
  {
    icon: Users,
    title: "Multi-utilisateurs",
    description: "Gérez votre équipe avec des rôles et permissions adaptés à chaque profil.",
  },
  {
    icon: Bell,
    title: "Notifications",
    description: "Recevez un email instantané pour chaque appel qualifié. Votre équipe peut s'attribuer les tickets.",
  },
  {
    icon: Download,
    title: "Export des données",
    description: "Exportez vos données au format CSV pour vos reportings et analyses.",
  },
];

const configFeatures = [
  {
    icon: Sliders,
    title: "Personnalisation du bot",
    description: "Configurez le ton, les questions posées et les critères de qualification selon vos besoins.",
  },
  {
    icon: Target,
    title: "Intentions personnalisées",
    description: "Ajoutez vos propres intentions à détecter au-delà des 8 préréglages immobiliers.",
  },
  {
    icon: HelpCircle,
    title: "FAQ personnalisée",
    description: "Le bot répond aux questions fréquentes (horaires, services, secteur) selon vos consignes.",
  },
  {
    icon: PhoneForwarded,
    title: "Transferts à chaud",
    description: "L'IA transfère l'appel vers le bon collaborateur selon le contexte et vos règles prédéfinies.",
  },
  {
    icon: Clock,
    title: "Mise en place rapide",
    description: "Opérationnel en 48h. En autonomie ou avec un accompagnement personnalisé sur rendez-vous.",
  },
];

const securityFeatures = [
  {
    icon: Server,
    title: "Hébergement Europe",
    description: "Vos données sont hébergées en France et en Europe pour une latence optimale.",
  },
  {
    icon: Shield,
    title: "Conformité RGPD",
    description: "Respect total du Règlement Général sur la Protection des Données.",
  },
  {
    icon: Lock,
    title: "Données chiffrées",
    description: "Chiffrement de bout en bout pour toutes les communications et données stockées.",
  },
  {
    icon: Trash2,
    title: "Suppression sur demande",
    description: "Exercez votre droit à l'oubli : suppression complète des données sur simple demande.",
  },
];

const Fonctionnalites = () => {
  const [calendlyOpen, setCalendlyOpen] = useState(false);
  const heroRef = useScrollReveal();
  const qualificationRef = useScrollReveal();
  const dashboardRef = useScrollReveal();
  const configRef = useScrollReveal();
  const securityRef = useScrollReveal();
  const ctaRef = useScrollReveal();

  useEffect(() => {
    // title set by SEOHead
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Fonctionnalités Immovox : appels, qualification et suivi"
        description="Découvrez les fonctionnalités Immovox : accueil des appels, qualification par scénario, synthèses, alertes et suivi des demandes immobilières."
        canonical="/fonctionnalites"
      />
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-background">
        <div className="container mx-auto px-4">
          <div
            ref={heroRef.ref}
            className="max-w-4xl"
            style={{
              opacity: heroRef.isVisible ? 1 : 0,
              transform: heroRef.isVisible ? "translateY(0)" : "translateY(30px)",
              transition: "all 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            <span className="eyebrow mb-6">
              Fonctionnalités
            </span>
            <h1 className="text-4xl md:text-6xl font-semibold tracking-[-.045em] text-foreground mb-6 leading-[1.03]">
              Tout ce dont vous avez besoin pour{" "}
              <span className="text-primary">qualifier vos appels</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Découvrez les outils qui transforment chaque appel entrant en opportunité qualifiée, 
              sans effort de votre part.
            </p>
            <Button
              size="lg"
              onClick={() => setCalendlyOpen(true)}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Planifier une démo
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Qualification Section */}
      <section className="py-20 bg-secondary">
        <div className="container mx-auto px-4">
          <div
            ref={qualificationRef.ref}
            className="mb-12"
            style={{
              opacity: qualificationRef.isVisible ? 1 : 0,
              transform: qualificationRef.isVisible ? "translateY(0)" : "translateY(30px)",
              transition: "all 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            <div className="w-10 h-10 border border-border flex items-center justify-center mb-5">
              <Brain className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-[-.03em] text-foreground mb-4">
              Qualification intelligente
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Notre IA analyse chaque appel en temps réel pour extraire les informations 
              essentielles et qualifier vos prospects automatiquement.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {qualificationFeatures.map((feature, index) => (
              <FeatureCard key={feature.title} {...feature} delay={index * 100} />
            ))}
          </div>
        </div>
      </section>

      {/* Dashboard Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div
            ref={dashboardRef.ref}
            className="mb-12"
            style={{
              opacity: dashboardRef.isVisible ? 1 : 0,
              transform: dashboardRef.isVisible ? "translateY(0)" : "translateY(30px)",
              transition: "all 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            <div className="w-10 h-10 border border-border flex items-center justify-center mb-5">
              <BarChart3 className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-[-.03em] text-foreground mb-4">
              Dashboard & Suivi
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Un tableau de bord intuitif pour visualiser, filtrer et agir sur tous vos appels qualifiés.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {dashboardFeatures.map((feature, index) => (
              <FeatureCard key={feature.title} {...feature} delay={index * 100} />
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/cas-dusage">
              <Button variant="outline" className="group">
                Voir les cas d'usage concrets
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Configuration Section */}
      <section className="py-20 bg-secondary">
        <div className="container mx-auto px-4">
          <div
            ref={configRef.ref}
            className="mb-12"
            style={{
              opacity: configRef.isVisible ? 1 : 0,
              transform: configRef.isVisible ? "translateY(0)" : "translateY(30px)",
              transition: "all 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            <div className="w-10 h-10 border border-border flex items-center justify-center mb-5">
              <Settings className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-[-.03em] text-foreground mb-4">
              Intégration & Configuration
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Personnalisez votre assistant vocal selon vos processus métier et vos besoins spécifiques.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {configFeatures.map((feature, index) => (
              <FeatureCard key={feature.title} {...feature} delay={index * 100} />
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div
            ref={securityRef.ref}
            className="mb-12"
            style={{
              opacity: securityRef.isVisible ? 1 : 0,
              transform: securityRef.isVisible ? "translateY(0)" : "translateY(30px)",
              transition: "all 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            <div className="w-10 h-10 border border-border flex items-center justify-center mb-5">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-[-.03em] text-foreground mb-4">
              Sécurité & Conformité
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Vos données et celles de vos clients sont protégées selon les standards les plus exigeants.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {securityFeatures.map((feature, index) => (
              <FeatureCard key={feature.title} {...feature} delay={index * 100} />
            ))}
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="py-12 bg-[hsl(157_24%_15%)]">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-primary-foreground mb-2">100%</div>
              <div className="text-primary-foreground/80">Automatisé</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary-foreground mb-2">15 min</div>
              <div className="text-primary-foreground/80">Gagnées par appel</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary-foreground mb-2">48h</div>
              <div className="text-primary-foreground/80">Mise en place</div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-secondary">
        <div className="container mx-auto px-4">
          <div
            ref={ctaRef.ref}
            className="max-w-2xl mx-auto text-center"
            style={{
              opacity: ctaRef.isVisible ? 1 : 0,
              transform: ctaRef.isVisible ? "translateY(0)" : "translateY(30px)",
              transition: "all 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold tracking-[-.03em] text-foreground mb-6">
              Prêt à transformer vos appels ?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Rejoignez les agences immobilières qui gagnent du temps et ne ratent plus aucune opportunité.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => setCalendlyOpen(true)}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Calendar className="mr-2 h-5 w-5" />
                Planifier une démo
              </Button>
              <Link to="/contact">
                <Button size="lg" variant="outline">
                  Nous contacter
                </Button>
              </Link>
            </div>
            <div className="mt-6 flex flex-wrap justify-center gap-4 text-sm">
              <Link to="/tarifs" className="text-primary-foreground/70 hover:text-primary-foreground underline">Voir nos tarifs →</Link>
              <Link to="/standard-telephonique-immobilier" className="text-primary-foreground/70 hover:text-primary-foreground underline">Standard téléphonique immobilier →</Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <CalendlyModal open={calendlyOpen} onOpenChange={setCalendlyOpen} />
      <ExitIntentPopup />
    </div>
  );
};

export default Fonctionnalites;
